import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useContext,
} from "react";
import { SimpleDialog, Button, SimpleUtility } from "simple-ui-components";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CheckIcon from "@mui/icons-material/Check";

import { AuthContext } from "../authContextProvider";
import { post } from "../../server-requests/serverRequests";
import { UpdateEquipments } from "./updateEquipments";
import { UpdateTechnician } from "./updateTechnician";
import { getTranslation } from "../languageProvider";
import { selectNextOnEnter, UpdateInputChange, Log } from "../shared/utility";
import FormHooks from "../shared/formHooks";
import Documents from "./documents";

const titleElement = (currentStage) => {
  switch (currentStage) {
    case "equipment":
      return `${getTranslation("0087")} ${getTranslation(
        "0071"
      ).toLowerCase()}`;
    case "technician":
      return `${getTranslation("0087")} ${getTranslation(
        "0088"
      ).toLowerCase()}`;
    case "documents":
      return getTranslation("0176");
    default:
      return `${getTranslation("0087")} ${getTranslation(
        "0008"
      ).toLowerCase()}`;
  }
};

export const AddRoutes = forwardRef(
  ({ UpdateSuccess, equipmentList, technicianList }, ref) => {
    const { Dialog, DialogBody, DialogFooter } = SimpleDialog;
    const { UpdateObject } = SimpleUtility;

    const { snackbarHandler } = useContext(AuthContext);

    const routeNameRef = useRef();
    const observationsRef = useRef();

    const [open, setOpen] = useState(false);
    const [currentStage, setCurrentStage] = useState("createRoute"); // name,equipment,technician

    const [currentRoute, setCurrentRoute] = useState({});

    const [formInputs, setFormInputs] = useState({
      name: {
        type: "input",
        label: "0132",
        value: "",
        validation: {
          required: true,
          min: 1,
          max: 30,
        },
        valid: false,
        touched: false,
        inputRef: routeNameRef,
        input: {
          type: "text",
          onKeyDown: (e) => selectNextOnEnter(e, observationsRef),
        },
      },
      observations: {
        type: "textarea",
        label: "0016",
        value: "",
        validation: {
          required: false,
          min: 0,
          max: 100,
        },
        valid: true,
        touched: false,
        inputRef: observationsRef,
        input: {
          rows: 10,
        },
      },
      submit: {
        valid: false,
        isSubmit: false,
      },
    });

    const inputChangeHandler = (e, key) =>
      setFormInputs(UpdateInputChange(formInputs, e.target.value, key));

    const handleClose = () => setOpen(false);

    // trigger from props
    useImperativeHandle(ref, () => ({
      RoutesHandler() {
        setOpen(true);
        setCurrentStage("createRoute");
        setFormInputs((prev) =>
          UpdateObject(prev, {
            name: UpdateObject(prev.name, {
              value: "",
              valid: false,
              touched: false,
            }),
            observations: UpdateObject(prev.observations, {
              value: "",
              valid: true,
              touched: false,
            }),
            submit: UpdateObject(prev.submit, {
              valid: false,
            }),
          })
        );
      },
    }));

    const clickPreviousHandler = () => {
      if (currentStage === "documents") {
        setCurrentStage("technician");
      } else if (currentStage === "technician") {
        setCurrentStage("equipment");
      }
    };

    const clickAddEditHandler = () => {
      if (currentStage === "createRoute") {
        let toSend = {
          name: formInputs.name.value,
          observations: formInputs.observations.value,
        };
        post("routes", toSend)
          .then((account) => {
            setCurrentRoute(account);
            setCurrentStage("equipment");
            UpdateSuccess();
          })
          .catch((status) => {
            snackbarHandler(getTranslation(`error${status}`));
            Log("Error on create /routes" + status);
          });
      } else if (currentStage === "equipment") {
        setCurrentStage("technician");
      } else if (currentStage === "technician") {
        setCurrentStage("documents");
      } else {
        setOpen(false);
      }
    };

    let previousButtonElement = null;
    if (currentStage === "documents" || currentStage === "technician")
      previousButtonElement = (
        <Button
          type="button"
          clicked={clickPreviousHandler}
          icon={<ArrowBackIosIcon />}
          text={getTranslation("0134")}
          className="btn-primary"
          theme="default"
        />
      );

    let dialogSize = "xl";
    if (currentStage === "createRoute" || currentStage === "documents")
      dialogSize = "sm";

    const addRouteElement = (state) => {
      switch (state) {
        case "equipment":
          return (
            <UpdateEquipments
              equipmentListFull={equipmentList}
              Success={UpdateSuccess}
              current={currentRoute}
              getSelected={false}
            />
          );
        case "technician":
          return (
            <UpdateTechnician
              technicianList={technicianList}
              current={currentRoute}
              Success={UpdateSuccess}
            />
          );
        case "documents":
          return <Documents currentRoute={currentRoute} />;
        default:
          return (
            <FormHooks
              formInputs={formInputs}
              inputChange={inputChangeHandler}
              onSubmit={clickAddEditHandler}
              size="md"
              layout="outline"
            />
          );
      }
    };

    if (!open) return null;
    return (
      <Dialog
        theme="default"
        size={dialogSize}
        close={handleClose}
        open={open}
        title={titleElement(currentStage)}
      >
        <DialogBody className="outline md">
          {addRouteElement(currentStage)}
        </DialogBody>
        <DialogFooter multiple={true}>
          {previousButtonElement}
          <Button
            type="button"
            clicked={clickAddEditHandler}
            icon={
              currentStage !== "documents" ? (
                <ArrowForwardIosIcon />
              ) : (
                <CheckIcon />
              )
            }
            text={
              currentStage !== "documents"
                ? getTranslation("0133")
                : getTranslation("0041")
            }
            className="btn-primary"
            theme="default"
            style={{ marginLeft: "auto" }}
          />
        </DialogFooter>
      </Dialog>
    );
  }
);
